/**
 * useUploadFlow · Owns Knowledge Graph upload state plus reindex flow.
 *
 * Rehomes the following state from KnowledgeGraphPage.jsx (pre-extraction
 * line numbers in parentheses):
 *   - selectedFile         (line 98)   · single-file selection for forecast or KG upload.
 *   - selectedFiles        (line 104)  · multi-file selection for batch KG upload.
 *   - uploadPartition      (line 105)  · destination partition string.
 *   - batchProgress        (line 106)  · { done · total · ok · failed · currentName } | null.
 *   - isUploading          (line 107)  · request-in-flight flag.
 *   - reindexPrompt        (line 116)  · { partition · since · upload_count } | null.
 *   - reindexRunning       (line 117)  · reindex-in-flight flag.
 *   - reindexProgress      (line 118)  · { phase · progress } | null · async poll UI.
 *
 * Rehomes the following handlers from the same file (pre-extraction line
 * numbers in parentheses):
 *   - handleInlineForecastUpload (1088)
 *   - handleFileUpload           (1101)
 *   - handleBatchUpload          (1145)
 *   - handleReindexConfirm       (1204)
 *   - handleReindexSkip          (1271)
 *
 * Extracted 2026-08-01 · IJ-244 · Phase 5C of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md Section Three Boundary Two).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor Programme 2026.
 * Owner: E1.
 *
 * Behaviour is preserved verbatim. Every toast message, every partition-name
 * regex, every axios URL, every fetch-state-refresh chain remains identical.
 *
 * Usage:
 *   const upload = useUploadFlow({ fetchStats, fetchDocuments, fetchEntities });
 *   const { selectedFile, handleFileUpload, ... } = upload;
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { API, authAxios } from '../utils/authClient';

export const useUploadFlow = ({ fetchStats, fetchDocuments, fetchEntities }) => {
  // Instantiate the authed axios client once. The factory returns an axios
  // instance with the Bearer-token interceptor wired in. Previous code
  // imported the factory renamed as `api` and never called it, so `api.post`
  // resolved to `authAxios.post` (undefined) and every upload threw
  // "authAxios.post is not a function" client-side. Root-caused 2026-08-11.
  const api = authAxios();
  const [selectedFile, setSelectedFile] = useState(null);
  // Batch KG upload (Feb 2026): selectedFiles is the multi-file array
  // set when the KG icon file-input receives >1 file. Kept as a sibling
  // state to `selectedFile` so the existing single-file forecast flow
  // remains untouched. `uploadPartition` names the tenant the batch
  // lands in; editable in the UI, defaults to the active Search.Inc thesis tenant.
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadPartition, setUploadPartition] = useState('searchinc_thesis_2026');
  const [batchProgress, setBatchProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // KA Incremental Reindex — operator-approved 2026-07-06 (Mockup v0.6):
  //   "If the user has loaded in new artefact(s) the system should
  //    probably ask at that time if the user wants to refresh the KA.
  //    That removes the need for a refresh button."
  // After ANY successful upload (single or batch) we set
  // `reindexPrompt` to open a modal with the exact operator wording.
  // If accepted, POST /api/ka/reindex?partition=X and toast the outcome.
  const [reindexPrompt, setReindexPrompt] = useState(null); // {partition, since, upload_count}
  const [reindexRunning, setReindexRunning] = useState(false);
  const [reindexProgress, setReindexProgress] = useState(null); // {phase, progress} | null — async poll UI

  const handleInlineForecastUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setSelectedFile(file);
    toast.success(`File "${file.name}" selected. Click "Generate Forecast" to proceed.`);
  };

  // Poll the async upload status endpoint until extraction settles.
  // Backend split (ij_upload_async_split_p0 · 2026-08-11) — the initial
  // POST returns 200 in ~3 s with extraction_status='processing' and
  // the slow LLM work runs in a background task. Poll every 2 s until
  // status flips to 'complete' or 'error'. Cap at 5 min so the hook
  // cannot spin forever.
  const _pollExtractionStatus = (documentId) => new Promise((resolve) => {
    const startedAt = Date.now();
    const POLL_MS = 2000;
    const CAP_MS = 5 * 60 * 1000;
    const tick = async () => {
      if (Date.now() - startedAt > CAP_MS) {
        resolve({ extraction_status: 'timeout' });
        return;
      }
      try {
        const r = await api.get(`${API}/documents/upload/status/${documentId}`);
        const s = r.data?.extraction_status;
        if (s === 'complete' || s === 'error') {
          resolve(r.data);
          return;
        }
      } catch (_e) {
        // transient — keep polling; final settle will resolve or timeout
      }
      setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
  });

  const handleFileUpload = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    setIsUploading(true);
    const uploadStartedAt = new Date().toISOString();
    const partition = uploadPartition || 'searchinc_thesis_2026';
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('partition', partition);
    try {
      const response = await api.post(`${API}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const documentId = response.data?.document_id;
      toast.success(`Uploaded "${selectedFile.name}" — extracting entities…`);
      setSelectedFile(null);
      setSelectedFiles([]);
      document.getElementById('file-input').value = '';
      // Poll the async extraction status. Fires the final toast when
      // the background task lands (complete / error / timeout).
      _pollExtractionStatus(documentId).then((final) => {
        if (final.extraction_status === 'complete') {
          const warn = final.extraction_warning;
          if (warn) {
            toast.error(
              `Uploaded but extraction had a warning: ${String(warn).substring(0, 220)}`,
              { duration: 10000 }
            );
          } else {
            toast.success(`Processed! ${final.entities_extracted} entities, ${final.relationships_extracted} relationships`);
          }
        } else if (final.extraction_status === 'error') {
          toast.error(
            `Extraction failed: ${String(final.extraction_warning || 'unknown error').substring(0, 220)}`,
            { duration: 10000 }
          );
        } else {
          toast.error('Extraction is taking longer than 5 minutes. Refresh the dashboard shortly to see the entities.');
        }
        fetchStats(); fetchDocuments(); fetchEntities();
      });
      // Multi-Topic v0.6 — prompt operator to refresh the KA now that
      // a new artefact has landed.
      setReindexPrompt({
        partition,
        since: uploadStartedAt,
        upload_count: 1,
      });
    } catch (error) {
      // Diagnostic surface — show the actual reason so the operator does
      // not have to open developer tools to see it. Covers three cases:
      // (a) server responded with an error status; (b) request went out
      // but no response arrived; (c) request never left the browser.
      let reason = 'unknown error';
      if (error?.response) {
        reason = `server returned ${error.response.status} ${error.response.statusText || ''}`.trim();
      } else if (error?.request) {
        reason = 'no response from server (network timeout, blocked, or dropped)';
      } else if (error?.message) {
        reason = `browser could not send request: ${error.message}`;
      }
      toast.error(`Failed to upload document — ${reason}`, { duration: 15000 });
    } finally {
      setIsUploading(false);
    }
  };

  // Batch KG upload — sequential per file so backend ingest (text
  // extract + NER + embeddings) doesn't get throttled by parallel
  // requests. Live progress toasts + a progress bar so the operator
  // sees the batch advancing on 15-20 doc runs.
  const handleBatchUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('No files selected'); return;
    }
    const partition = (uploadPartition || '').trim().toLowerCase();
    if (!/^[a-z0-9_]{1,64}$/.test(partition)) {
      toast.error('Invalid partition name. Use 1–64 chars of [a-z0-9_].');
      return;
    }
    const uploadStartedAt = new Date().toISOString();
    setIsUploading(true);
    setBatchProgress({ done: 0, total: selectedFiles.length,
                       ok: 0, failed: 0, currentName: '' });
    let ok = 0, failed = 0;
    const failedNames = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const f = selectedFiles[i];
      setBatchProgress({ done: i, total: selectedFiles.length,
                         ok, failed, currentName: f.name });
      const fd = new FormData();
      fd.append('file', f);
      fd.append('partition', partition);
      try {
        const resp = await api.post(`${API}/documents/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' } });
        // Wait for the background extraction so the batch progress
        // bar reflects real completion, not just the fast-path accept.
        const docId = resp.data?.document_id;
        if (docId) {
          const final = await _pollExtractionStatus(docId);
          if (final.extraction_status === 'error') {
            failed++;
            failedNames.push(f.name);
          } else {
            ok++;
          }
        } else {
          ok++;
        }
      } catch (err) {
        failed++;
        failedNames.push(f.name);
      }
    }
    setBatchProgress({ done: selectedFiles.length, total: selectedFiles.length,
                       ok, failed, currentName: '' });
    setIsUploading(false);
    if (failed === 0) {
      toast.success(`Batch complete: ${ok}/${selectedFiles.length} uploaded to partition '${partition}'.`);
    } else {
      toast.error(`Batch complete: ${ok} ok · ${failed} failed. Failed: ${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? '…' : ''}`);
    }
    setSelectedFile(null);
    setSelectedFiles([]);
    document.getElementById('file-input').value = '';
    fetchStats(); fetchDocuments(); fetchEntities();
    // Multi-Topic v0.6 — prompt operator to refresh KA if at least one
    // artefact landed successfully.
    if (ok > 0) {
      setReindexPrompt({
        partition,
        since: uploadStartedAt,
        upload_count: ok,
      });
    }
  };

  // Confirm the KA reindex prompt. Enqueues an async /api/ka/reindex
  // job then polls /api/ka/reindex/status/{job_id} every 2 s. The
  // async path (default since 2026-02-14) keeps the request under the
  // Cloudflare / K8s ingress ~60 s timeout on large partitions.
  // Never dismisses on failure so the operator can retry.
  const handleReindexConfirm = async () => {
    if (!reindexPrompt) return;
    setReindexRunning(true);
    setReindexProgress({ phase: 'queued', progress: 0 });
    let jobId = null;
    try {
      const enqueue = await api.post(`${API}/ka/reindex`, {
        partition: reindexPrompt.partition,
        since:     reindexPrompt.since,
        mode:      'delta',
      });
      jobId = enqueue.data?.job_id;
      if (!jobId) {
        // Backend somehow returned the legacy sync payload — fall
        // back to the old success path so we do not regress ops.
        const m3 = enqueue.data?.m3 || {};
        const m4 = enqueue.data?.m4 || {};
        toast.success(
          `Knowledge Atlas refreshed · ${m3.ok || 0} new Concept commits · ${m4.total_written || 0} new Connections · ${enqueue.data?.duration_ms || 0} ms`
        );
        try {
          const iso = reindexPrompt.since || new Date().toISOString();
          window.sessionStorage.setItem(
            `ka_new_since_${reindexPrompt.partition}`, iso);
        } catch (_) { /* private-mode noop */ }
        setReindexPrompt(null);
        return;
      }
      // Poll every 2 s, cap at 15 min (450 iterations).
      const deadlineAt = Date.now() + 15 * 60 * 1000;
      while (true) {
        if (Date.now() > deadlineAt) {
          throw new Error('Reindex timed out after 15 minutes');
        }
        await new Promise((res) => setTimeout(res, 2000));
        const poll = await api.get(`${API}/ka/reindex/status/${jobId}`);
        const s = poll.data || {};
        setReindexProgress({
          phase:    s.phase    || s.status || 'running',
          progress: s.progress || 0,
        });
        if (s.status === 'done') {
          const m3 = s.result?.m3 || {};
          const m4 = s.result?.m4 || {};
          try {
            const iso = reindexPrompt.since || new Date().toISOString();
            window.sessionStorage.setItem(
              `ka_new_since_${reindexPrompt.partition}`, iso);
          } catch (_) { /* private-mode noop */ }
          toast.success(
            `Knowledge Atlas refreshed · ${m3.ok || 0} new Concept commits · ${m4.total_written || 0} new Connections · ${s.result?.duration_ms || 0} ms`
          );
          setReindexPrompt(null);
          return;
        }
        if (s.status === 'failed') {
          throw new Error(s.error || 'reindex job failed');
        }
      }
    } catch (err) {
      toast.error(`Reindex failed: ${err.message || 'unknown error'}`);
    } finally {
      setReindexRunning(false);
      setReindexProgress(null);
    }
  };

  const handleReindexSkip = () => {
    setReindexPrompt(null);
  };

  return {
    // State
    selectedFile,
    selectedFiles,
    uploadPartition,
    batchProgress,
    isUploading,
    reindexPrompt,
    reindexRunning,
    reindexProgress,
    // Setters exposed for the file-input onChange handler in UploadPanel,
    // the Clear button in the batch panel, and the Phase 5F forecast flow
    // which needs to reset selectedFile after a successful forecast run.
    setSelectedFile,
    setSelectedFiles,
    setUploadPartition,
    setBatchProgress,
    setIsUploading,
    setReindexPrompt,
    // Handlers
    handleInlineForecastUpload,
    handleFileUpload,
    handleBatchUpload,
    handleReindexConfirm,
    handleReindexSkip,
  };
};

export default useUploadFlow;
